(() => {
  const GALLERY_SELECTOR = '[data-vb-gallery]';
  const PRODUCT_SELECTOR = '[data-vb-product-root]';

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.vbReady === 'true') return;

    const track = gallery.querySelector('[data-vb-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-vb-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-vb-thumb]'));
    const prev = gallery.querySelector('[data-vb-prev]');
    const next = gallery.querySelector('[data-vb-next]');
    const current = gallery.querySelector('[data-vb-current]');
    if (!track || !slides.length) return;

    gallery.dataset.vbReady = 'true';
    let index = 0;
    let startX = null;
    let startY = null;

    const normalize = (value) => (value + slides.length) % slides.length;

    const pauseInactiveMedia = () => {
      slides.forEach((slide, slideIndex) => {
        if (slideIndex === index) return;
        slide.querySelectorAll('video').forEach((video) => {
          if (typeof video.pause === 'function') video.pause();
        });
      });
    };

    const go = (value, focusThumb = false) => {
      index = normalize(value);
      track.style.transform = `translate3d(${-index * 100}%,0,0)`;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !active;
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
          if (focusThumb) thumb.focus({ preventScroll: true });
        }
      });
      if (current) current.textContent = String(index + 1);
      pauseInactiveMedia();
    };

    prev?.addEventListener('click', () => go(index - 1));
    next?.addEventListener('click', () => go(index + 1));
    thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => go(thumbIndex)));

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      startX = event.clientX;
      startY = event.clientY;
    });

    track.addEventListener('pointerup', (event) => {
      if (startX === null || startY === null) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) go(index + (dx < 0 ? 1 : -1));
      startX = null;
      startY = null;
    });

    track.addEventListener('pointercancel', () => {
      startX = null;
      startY = null;
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(index - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(index + 1, true);
      }
    });

    go(0);
  }

  // Mirrors Shopify's money filter so JS-updated prices match the Liquid-rendered ones.
  function moneyFormatter(root) {
    const format = root.dataset.moneyFormat || '{{amount}}';

    const withGrouping = (number, precision, thousands, decimal) => {
      if (!Number.isFinite(number)) return '0';
      const fixed = Math.abs(number).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+$)/g, `$1${thousands}`);
      const sign = number < 0 ? '-' : '';
      return sign + grouped + (fraction ? decimal + fraction : '');
    };

    return {
      format(amount) {
        const value = Number(amount) || 0;
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, name) => {
          switch (name) {
            case 'amount_no_decimals':
              return withGrouping(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return withGrouping(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withGrouping(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withGrouping(value, 2, "'", '.');
            case 'amount_no_decimals_with_space_separator':
              return withGrouping(value, 0, ' ', ',');
            case 'amount_with_space_separator':
              return withGrouping(value, 2, ' ', ',');
            default:
              return withGrouping(value, 2, ',', '.');
          }
        });
      }
    };
  }

  function readGifts(root) {
    const node = root.querySelector('[data-vb-gifts]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed.filter((gift) => gift && gift.id) : [];
    } catch (_) {
      return [];
    }
  }

  function initProduct(root) {
    if (!root || root.dataset.vbProductReady === 'true') return;
    root.dataset.vbProductReady = 'true';

    const variantSelect = root.querySelector('[data-vb-variant]');
    const singleVariant = root.querySelector('[data-vb-variant-single]');
    const price = root.querySelector('[data-vb-price]');
    const compare = root.querySelector('[data-vb-compare]');
    const quantityInput = root.querySelector('[data-vb-quantity]');
    const bundleChoices = Array.from(root.querySelectorAll('[data-vb-bundle-choice]'));
    const bundleTotals = Array.from(root.querySelectorAll('[data-vb-bundle-total]'));
    const unitPrices = Array.from(root.querySelectorAll('[data-vb-unit-price]'));
    const bundleSavings = Array.from(root.querySelectorAll('[data-vb-bundle-save]'));
    const discount = root.querySelector('[data-vb-discount]');
    const formatter = moneyFormatter(root);

    const currentVariantData = () => {
      if (variantSelect) {
        const option = variantSelect.options[variantSelect.selectedIndex];
        return {
          priceCents: Number(option?.dataset.priceCents || 0),
          compareCents: Number(option?.dataset.compareCents || 0),
          priceText: option?.dataset.price || '',
          compareText: option?.dataset.compare || ''
        };
      }
      return {
        priceCents: Number(singleVariant?.dataset.priceCents || 0),
        compareCents: Number(singleVariant?.dataset.compareCents || 0),
        priceText: price?.textContent?.trim() || '',
        compareText: compare?.textContent?.trim() || ''
      };
    };

    const updateBundlePrices = () => {
      const variant = currentVariantData();
      const savedPerUnit = Math.max(0, variant.compareCents - variant.priceCents);
      bundleTotals.forEach((node) => {
        const qty = Number(node.dataset.quantity || 1);
        node.textContent = formatter.format((variant.priceCents * qty) / 100);
      });
      unitPrices.forEach((node) => {
        node.textContent = formatter.format(variant.priceCents / 100);
      });
      bundleSavings.forEach((node) => {
        const qty = Number(node.dataset.quantity || 1);
        const saved = savedPerUnit * qty;
        node.hidden = saved <= 0;
        if (saved > 0) node.textContent = `${node.dataset.savingLabel || ''} ${formatter.format(saved / 100)}`.trim();
      });
    };

    const updateVariantPrice = () => {
      const variant = currentVariantData();
      if (price) price.textContent = variant.priceText || formatter.format(variant.priceCents / 100);
      const onSale = variant.compareCents > variant.priceCents;
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? variant.compareText || formatter.format(variant.compareCents / 100) : '';
      }
      if (discount) {
        discount.hidden = !onSale;
        if (onSale) {
          if (discount.dataset.badgeFormat === 'amount') {
            const saved = (variant.compareCents - variant.priceCents) / 100;
            discount.textContent = `${discount.dataset.savingLabel || ''} ${formatter.format(saved)}`.trim();
          } else {
            discount.textContent = `-${Math.round(((variant.compareCents - variant.priceCents) / variant.compareCents) * 100)}%`;
          }
        }
      }
      updateBundlePrices();
    };

    const updateQuantity = (choice) => {
      if (!quantityInput || !choice) return;
      const qty = Math.max(1, Number(choice.dataset.quantity || 1));
      quantityInput.value = String(qty);
    };

    const stickyBar = document.querySelector(`[data-vb-sticky="${root.dataset.vbSectionId}"]`);
    const stickyPrice = stickyBar?.querySelector('[data-vb-sticky-price]');
    const updateStickyPrice = () => {
      if (!stickyPrice) return;
      const qty = Math.max(1, Number(quantityInput?.value || 1));
      stickyPrice.textContent = formatter.format((currentVariantData().priceCents * qty) / 100);
    };

    variantSelect?.addEventListener('change', () => {
      updateVariantPrice();
      updateStickyPrice();
    });
    bundleChoices.forEach((choice) => {
      choice.addEventListener('change', () => {
        if (choice.checked) {
          updateQuantity(choice);
          updateStickyPrice();
        }
      });
    });

    const checked = bundleChoices.find((choice) => choice.checked) || bundleChoices[0];
    if (checked) updateQuantity(checked);
    updateVariantPrice();
    updateStickyPrice();

    initCart(root, { quantityInput, gifts: readGifts(root) });
    initSticky(root);
    initOfferTimer(root);
  }

  // Adds the selected pack plus every free gift as separate line items, then
  // refreshes the cart drawer. Gifts already in the cart are never re-added.
  function initCart(root, { quantityInput, gifts }) {
    const form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const errorBox = root.querySelector('[data-vb-error]');
    const showError = (message) => {
      if (!errorBox) return;
      errorBox.hidden = !message;
      errorBox.textContent = message || '';
    };

    const buttons = () => Array.from(root.querySelectorAll('[type="submit"][name="add"], .vb-sticky-btn'));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.vbBusy === 'true') return;
      form.dataset.vbBusy = 'true';
      showError('');
      buttons().forEach((button) => button.setAttribute('aria-busy', 'true'));

      try {
        const variantId = Number(new FormData(form).get('id'));
        if (!variantId) throw new Error('Selecciona una opción del producto.');
        const quantity = Math.max(1, Number(quantityInput?.value || 1));
        const items = [{ id: variantId, quantity }];

        if (gifts.length) {
          const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
            headers: { Accept: 'application/json' }
          }).then((response) => response.json()).catch(() => null);
          const inCart = new Set((cart?.items || []).map((item) => item.variant_id));
          gifts.forEach((gift) => {
            if (!inCart.has(gift.id)) items.push({ id: gift.id, quantity: 1 });
          });
        }

        const drawer = document.querySelector('cart-drawer');
        const body = { items };
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map((section) => section.id).join(',');
          body.sections_url = window.location.pathname;
        }

        const addUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'No se ha podido añadir al carrito.');

        if (drawer && typeof drawer.renderContents === 'function') {
          // Liquid stamps is-empty on first render; the drawer only clears it from its own flow.
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location = window.routes?.cart_url || '/cart';
        }
        document.dispatchEvent(new CustomEvent('vibalarma:cart-added', { detail: data }));
      } catch (error) {
        showError(error.message);
      } finally {
        form.dataset.vbBusy = 'false';
        buttons().forEach((button) => button.removeAttribute('aria-busy'));
      }
    });
  }

  // Show the fixed action whenever the actual purchase button is off screen.
  function initSticky(root) {
    const bar = document.querySelector(`[data-vb-sticky="${root.dataset.vbSectionId}"]`);
    const anchor = root.querySelector('[type="submit"][name="add"]');
    if (!bar || !anchor) return;

    let frame = null;
    const sync = () => {
      frame = null;
      const bounds = anchor.getBoundingClientRect();
      const visible = bounds.width > 0 && bounds.height > 0 &&
        bounds.bottom > 0 && bounds.top < window.innerHeight &&
        bounds.right > 0 && bounds.left < window.innerWidth;
      bar.hidden = visible;
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(sync);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    const observer = new IntersectionObserver(schedule);
    observer.observe(anchor);
    sync();
  }

  function initOfferTimer(root) {
    const timer = root.querySelector('[data-vb-offer-timer]');
    if (!timer || timer.dataset.initialized === 'true') return;
    timer.dataset.initialized = 'true';
    const endAt = Date.now() + 3 * 60 * 1000;
    const render = () => {
      const seconds = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      timer.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
      if (seconds > 0) window.setTimeout(render, 250);
    };
    render();
  }

  function initScope(scope = document) {
    scope.querySelectorAll?.(GALLERY_SELECTOR).forEach(initGallery);
    scope.querySelectorAll?.(PRODUCT_SELECTOR).forEach(initProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScope(document));
  } else {
    initScope(document);
  }

  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
})();
